import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, delay } from 'rxjs';
import { OrderShortModel, OrderShortSearchParams, OrderShortResponse, OrderShortActionResponse } from './order-short.model';

@Injectable({
  providedIn: 'root'
})
export class OrderShortService {
  // private http = inject(HttpClient);

  // Mock data for demonstration
  private mockOrders: OrderShortModel[] = [
    {
      id: '1',
      thirdPartyOrderNumber: 'a',
      internalOrderNumber: 'CO00000009',
      tags: 'Order Shortage',
      reviewStatus: 'Pending Review',
      salesStore: 'Colorful Good Goods Warehouse',
      salesChannel: 'Shopee',
      orderSource: 'Manual Order',
      productLineCount: 1,
      shortageCount: 1,
      buyerNickname: 'sd',
      buyerNotes: 'a',
      merchantNotes: 'a',
      receiverName: 'c',
      receiverPhone: '136****0299',
      receiverAddress: 'HCM City, Vietnam',
      totalProductAmount: 0.00,
      buyerPayable: 0.00,
      totalProductCost: 2.00,
      thirdPartyOrderTime: '2025-08-01 00:00:00',
      paymentTime: '2025-08-22 00:00:00',
      internalCreationTime: '2025-08-05 19:24:25',
      expectedShipmentTime: '',
      suspendStatus: 'Not Suspended',
      giftStatus: 'No'
    },
    {
      id: '2',
      thirdPartyOrderNumber: 'OD202511250001',
      internalOrderNumber: 'CO00000017',
      tags: 'Order Shortage',
      reviewStatus: 'Pending Review',
      salesStore: 'Trend Station',
      salesChannel: 'Shopee',
      orderSource: 'Manual Order',
      productLineCount: 21,
      shortageCount: 21,
      buyerNickname: 'Zhang San',
      buyerNotes: 'Remarks',
      merchantNotes: 'Remarks',
      receiverName: 'Zhang San',
      receiverPhone: '181****9966',
      receiverAddress: 'HCM City, Vietnam',
      totalProductAmount: 5230.00,
      buyerPayable: 5230.00,
      totalProductCost: 3800.00,
      thirdPartyOrderTime: '2025-05-06 00:00:00',
      paymentTime: '2025-05-09 00:00:00',
      internalCreationTime: '2025-05-07 09:07:55',
      expectedShipmentTime: '',
      suspendStatus: 'Not Suspended',
      giftStatus: 'Yes'
    },
    {
      id: '3',
      thirdPartyOrderNumber: 'test1112',
      internalOrderNumber: 'CO00000052',
      tags: 'Order Shortage',
      reviewStatus: 'Pending Review',
      salesStore: 'Colorful Good Goods Warehouse',
      salesChannel: 'Shopee',
      orderSource: 'Copy Order',
      productLineCount: 3,
      shortageCount: 3,
      buyerNickname: 'test',
      buyerNotes: '',
      merchantNotes: '',
      receiverName: 'test',
      receiverPhone: '136****0299',
      receiverAddress: 'HCM City, Vietnam',
      totalProductAmount: 10.00,
      buyerPayable: 10.00,
      totalProductCost: 2.00,
      thirdPartyOrderTime: '2025-04-01 00:00:00',
      paymentTime: '2025-04-02 16:06:24',
      internalCreationTime: '2025-04-02 16:08:39',
      expectedShipmentTime: '',
      suspendStatus: 'Not Suspended',
      giftStatus: 'Yes'
    },
    {
      id: '4',
      thirdPartyOrderNumber: 'test111',
      internalOrderNumber: 'CO00000047',
      tags: 'Order Shortage',
      reviewStatus: 'Pending Review',
      salesStore: 'Colorful Good Goods Warehouse',
      salesChannel: 'Shopee',
      orderSource: 'Normal Transfer Order',
      productLineCount: 1,
      shortageCount: 1,
      buyerNickname: 'test',
      buyerNotes: '',
      merchantNotes: '',
      receiverName: 'test',
      receiverPhone: '136****0299',
      receiverAddress: 'HCM City, Vietnam',
      totalProductAmount: 5.00,
      buyerPayable: 5.00,
      totalProductCost: 1.00,
      thirdPartyOrderTime: '2025-04-01 00:00:00',
      paymentTime: '2025-04-02 16:06:24',
      internalCreationTime: '2025-04-02 16:07:14',
      expectedShipmentTime: '',
      suspendStatus: 'Not Suspended',
      giftStatus: 'No'
    },
    {
      id: '5',
      thirdPartyOrderNumber: '454554',
      internalOrderNumber: 'CO00000037',
      tags: 'Order Shortage',
      reviewStatus: 'Pending Review',
      salesStore: 'Trend Station',
      salesChannel: 'Shopee',
      orderSource: 'Manual Order',
      productLineCount: 2,
      shortageCount: 2,
      buyerNickname: '224',
      buyerNotes: '45545',
      merchantNotes: '',
      receiverName: '5455',
      receiverPhone: '132****9112',
      receiverAddress: 'HCM City, Vietnam',
      totalProductAmount: 12.00,
      buyerPayable: 12.00,
      totalProductCost: 10.00,
      thirdPartyOrderTime: '2025-03-01 00:00:00',
      paymentTime: '2025-03-01 00:00:00',
      internalCreationTime: '2025-03-23 22:26:39',
      expectedShipmentTime: '',
      suspendStatus: 'Not Suspended',
      giftStatus: 'No'
    }
  ];

  getOrders(params: OrderShortSearchParams): Observable<OrderShortResponse> {
    // Simulate API delay
    return of({
      data: this.mockOrders,
      total: this.mockOrders.length,
      success: true,
      message: 'Orders retrieved successfully'
    });
  }

  processShortage(orderIds: string[]): Observable<OrderShortActionResponse> {
    // Simulate API call for processing shortage
    return of({
      success: true,
      message: `Shortage processing initiated for ${orderIds.length} orders`,
      affectedCount: orderIds.length
    }).pipe(delay(1000));
  }

  exportOrders(params: OrderShortSearchParams): Observable<string> {
    // Simulate CSV export
    const headers = [
      'Third Party Order Number',
      'Internal Order Number',
      'Tags',
      'Review Status',
      'Sales Store',
      'Sales Channel',
      'Order Source',
      'Product Line Count',
      'Shortage Count',
      'Buyer Nickname',
      'Buyer Notes',
      'Merchant Notes',
      'Receiver Name',
      'Receiver Phone',
      'Receiver Address',
      'Total Product Amount',
      'Buyer Payable',
      'Total Product Cost',
      'Third Party Order Time',
      'Payment Time',
      'Internal Creation Time',
      'Expected Shipment Time',
      'Suspend Status',
      'Gift Status'
    ];

    const rows = this.mockOrders.map(order => [
      order.thirdPartyOrderNumber,
      order.internalOrderNumber,
      order.tags,
      order.reviewStatus,
      order.salesStore,
      order.salesChannel,
      order.orderSource,
      order.productLineCount.toString(),
      order.shortageCount.toString(),
      order.buyerNickname,
      order.buyerNotes,
      order.merchantNotes,
      order.receiverName,
      order.receiverPhone,
      order.receiverAddress,
      order.totalProductAmount.toFixed(2),
      order.buyerPayable.toFixed(2),
      order.totalProductCost.toFixed(2),
      order.thirdPartyOrderTime,
      order.paymentTime,
      order.internalCreationTime,
      order.expectedShipmentTime,
      order.suspendStatus,
      order.giftStatus
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    return of(csvContent).pipe(delay(500));
  }
} 